/**
 * RelarkArticle14Native
 * ---------------------
 * Sharper "Article 14-native" positioning section.
 *
 * Sits between ComplianceSection and EscalationPromise on the Relark landing
 * page. Goal: convert "we tick the EU AI Act box" into the much more
 * differentiated story — Relark was *built around* Article 14 (human
 * oversight), not retrofitted to it.
 *
 * Three cards link to existing surfaces (no new infra):
 *   - Override-flow      → /trust#human-control
 *   - Provenance-badge   → /trust#xai (live <AiProvenanceBadge /> shown below)
 *   - Audit-spor         → /settings/audit (gated; visible to admins)
 *
 * Footer CTA scrolls to the existing AI Act compliance matrix on /trust.
 */

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, FileText, ArrowRight } from "lucide-react";
import { useScrollFadeIn, fadeCls, fadeStyle } from "@/hooks/useScrollFadeIn";
import { trackEvent } from "@/lib/analytics";
import { AiProvenanceBadge } from "@/components/AiProvenanceBadge";

type CardKey = "override" | "provenance" | "audit";

const CARDS: {
  key: CardKey;
  icon: typeof ShieldCheck;
  href: string;
  external?: boolean;
}[] = [
  { key: "override", icon: ShieldCheck, href: "/trust#human-control" },
  { key: "provenance", icon: Sparkles, href: "/trust#xai" },
  { key: "audit", icon: FileText, href: "/settings/audit" },
];

interface RelarkArticle14NativeProps {
  /**
   * When true, render without the outer <section> wrapper, background, and
   * vertical padding. Use this when embedding inside another section (e.g.
   * RelarkComplianceSection's tab panel) to avoid double backgrounds and
   * conflicting paddings.
   */
  embedded?: boolean;
}

export default function RelarkArticle14Native({ embedded = false }: RelarkArticle14NativeProps = {}) {
  const { t } = useTranslation("ui");
  const { ref, isVisible } = useScrollFadeIn();

  const inner = (
    <div ref={ref} className="mx-auto max-w-5xl">
        <div className={`text-center max-w-2xl mx-auto ${fadeCls(isVisible)}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
            {t("relark.article14.eyebrow")}
          </p>
          <h2 className="text-2xl font-bold sm:text-3xl">
            {t("relark.article14.title")}
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            {t("relark.article14.lede")}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {CARDS.map(({ key, icon: Icon, href, external }, i) => {
            const body = (
              <>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="mt-4 text-base font-semibold">
                  {t(`relark.article14.cards.${key}.title`)}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">
                  {t(`relark.article14.cards.${key}.body`)}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  {t(`relark.article14.cards.${key}.cta`)}
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </>
            );

            const cls = `group flex h-full flex-col rounded-2xl border-2 border-primary/15 bg-card p-6 shadow-sm transition hover:border-primary/40 hover:shadow-md ${fadeCls(isVisible)}`;
            const style = fadeStyle(150 + i * 120);

            if (external) {
              return (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("article14_card_click", { card: key })}
                  className={cls}
                  style={style}
                >
                  {body}
                </a>
              );
            }
            return (
              <Link
                key={key}
                to={href}
                onClick={() => trackEvent("article14_card_click", { card: key })}
                className={cls}
                style={style}
              >
                {body}
              </Link>
            );
          })}
        </div>

        {/* Live provenance-badge — shown directly so visitors see the real
            artefact, not just a description of it. */}
        <div
          className={`mt-10 flex justify-center ${fadeCls(isVisible)}`}
          style={fadeStyle(600)}
        >
          <AiProvenanceBadge />
        </div>

        <div
          className={`mt-8 text-center ${fadeCls(isVisible)}`}
          style={fadeStyle(700)}
        >
          <p className="text-sm text-muted-foreground">
            {t("relark.article14.footer")}
          </p>
          <Link
            to="/trust#high-risk-by-design"
            onClick={() => trackEvent("article14_matrix_cta_click")}
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline underline-offset-4"
          >
            {t("relark.article14.footerCta")}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
    </div>
  );

  if (embedded) {
    return inner;
  }

  return (
    <section id="article-14-native" className="bg-accent/20 py-16 sm:py-24">
      <div className="px-5">{inner}</div>
    </section>
  );
}
